import type {
  PlayerId,
  WarchestAction,
  WarchestState,
} from "@cloudnest/warchest-engine";
import {
  applyAttack,
  applyBolster,
  applyClaimInitiative,
  applyControl,
  applyDeploy,
  applyDraftPick,
  applyDrawPhase,
  applyMove,
  applyPass,
  applyRecruit,
  applyTactic,
  createGame,
  initDraftGame,
  redactGameState,
  validateAction,
} from "@cloudnest/warchest-engine";
import { useSyncExternalStore } from "react";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
  type ViewerStateRow,
} from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";
import { resolveWarchestGameApiMode } from "./route-mode";

export type WarchestGameMode = "quick" | "draft";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function getGameApiMode(): GameApiMode {
  return resolveWarchestGameApiMode();
}

function isGamePlatformMode(): boolean {
  return getGameApiMode() === "game-platform";
}

function getGameApi(): GameApi {
  const nextMode = getGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("warchest", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

export interface HistoryEntry {
  readonly sequenceNumber: number;
  readonly eventType: string;
  readonly payload: Record<string, unknown>;
}

interface MultiplayerGameState {
  readonly gameState: WarchestState | null;
  readonly roomId: string | null;
  readonly myPlayerId: PlayerId | null;
  readonly isHost: boolean;
  readonly lastAppliedSeq: number;
  readonly lastError: string | null;
  readonly selectedCoin: string | null;
  readonly legalTargets: readonly string[];
  readonly events: readonly HistoryEntry[];
  readonly participantUserIds: readonly string[];
  // Host has reached the game screen but has not yet chosen a game mode.
  // The host picks Quick Start / Draft before the `init` event is submitted.
  readonly needsHostSetup: boolean;
  readonly pendingInit: {
    readonly seed?: number;
    readonly p1UserId: string;
    readonly p2UserId: string;
  } | null;
}

const initialState: MultiplayerGameState = {
  gameState: null,
  roomId: null,
  myPlayerId: null,
  isHost: false,
  lastAppliedSeq: 0,
  lastError: null,
  selectedCoin: null,
  legalTargets: [],
  events: [],
  participantUserIds: [],
  needsHostSetup: false,
  pendingInit: null,
};

let state: MultiplayerGameState = { ...initialState };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function reconstructFromSeed(seed: number): WarchestState {
  return applyDrawPhase(createGame({ seed }));
}

function initStateFromPayload(payload: Record<string, unknown>): WarchestState {
  // E2E state-injection path: tests POST an init event with a literal
  // WarchestState in `payload.state` to start a room mid-game. See
  // e2e/_shared/warchest-fixtures.ts and docs/guides/multiplayer-e2e-guide.md
  // §State Injection.
  if (payload.state !== undefined) {
    return payload.state as WarchestState;
  }
  const seed = payload.seed as number;
  // `mode: "draft"` starts in the DRAFT phase; anything else (including a
  // missing mode, e.g. legacy/seed-only inits and E2E injections) is Quick
  // Start and goes straight to the first draw phase.
  if (payload.mode === "draft") {
    return initDraftGame({ seed });
  }
  return reconstructFromSeed(seed);
}

function applyEventToState(
  gameState: WarchestState,
  eventType: string,
  payload: Record<string, unknown>,
): WarchestState {
  switch (eventType) {
    case "init":
      return initStateFromPayload(payload);

    case "draw_phase":
      return applyDrawPhase(gameState);

    case "draft_pick": {
      const next = applyDraftPick(
        gameState,
        payload as unknown as Parameters<typeof applyDraftPick>[1],
      );
      // The engine's applyDraftPick transitions DRAFT → DRAW_COINS on the pick
      // that completes the draft, but does not deal the first hand. Mirror the
      // Quick-Start init (which bakes the draw into `init`) by drawing on that
      // completing pick so every client folds the log to the same state.
      // applyDrawPhase is itself phase-guarded, so this is safe to gate solely
      // on the post-apply phase.
      return next.phase === "DRAW_COINS" ? applyDrawPhase(next) : next;
    }

    case "deploy":
      return applyDeploy(
        gameState,
        payload as unknown as Parameters<typeof applyDeploy>[1],
      );

    case "bolster":
      return applyBolster(
        gameState,
        payload as unknown as Parameters<typeof applyBolster>[1],
      );

    case "claim_initiative":
      return applyClaimInitiative(
        gameState,
        payload as unknown as Parameters<typeof applyClaimInitiative>[1],
      );

    case "recruit":
      return applyRecruit(
        gameState,
        payload as unknown as Parameters<typeof applyRecruit>[1],
      );

    case "pass":
      return applyPass(
        gameState,
        payload as unknown as Parameters<typeof applyPass>[1],
      );

    case "move":
      return applyMove(
        gameState,
        payload as unknown as Parameters<typeof applyMove>[1],
      );

    case "control":
      return applyControl(
        gameState,
        payload as unknown as Parameters<typeof applyControl>[1],
      );

    case "attack":
      return applyAttack(
        gameState,
        payload as unknown as Parameters<typeof applyAttack>[1],
      );

    case "tactic":
      return applyTactic(
        gameState,
        payload as unknown as Parameters<typeof applyTactic>[1],
      );

    default:
      console.warn(`Unknown warchest event type: ${eventType}`);
      return gameState;
  }
}

function derivePlayerId(myUserId: string, p1UserId: string): PlayerId {
  return myUserId === p1UserId ? "P1" : "P2";
}

function derivePlayerIdFromParticipants(
  myUserId: string,
  participantUserIds: readonly string[],
): PlayerId | null {
  const [p1UserId, p2UserId] = participantUserIds;
  if (myUserId === p1UserId) return "P1";
  if (myUserId === p2UserId) return "P2";
  return null;
}

function actionToEventType(action: WarchestAction): string {
  return action.type.toLowerCase();
}

function appendEvent(
  eventType: string,
  payload: Record<string, unknown>,
  sequenceNumber: number,
): readonly HistoryEntry[] {
  if (eventType === "init") {
    return [{ sequenceNumber, eventType, payload }];
  }
  return [...state.events, { sequenceNumber, eventType, payload }];
}

function adoptViewerState(
  viewerState: Record<string, unknown>,
  sequenceNumber: number,
): boolean {
  if (sequenceNumber <= state.lastAppliedSeq) return false;
  state = {
    ...state,
    gameState: viewerState as unknown as WarchestState,
    lastAppliedSeq: sequenceNumber,
    selectedCoin: null,
    legalTargets: [],
    needsHostSetup: false,
    pendingInit: null,
    lastError: null,
  };
  return true;
}

function adoptLatestViewerState(
  rows: readonly ViewerStateRow[],
  fromSeq: number,
): boolean {
  let bestSeq = fromSeq;
  let best: Record<string, unknown> | null = null;
  for (const row of rows) {
    if (row.sequenceNumber > bestSeq) {
      bestSeq = row.sequenceNumber;
      best = row.state;
    }
  }
  return best === null ? false : adoptViewerState(best, bestSeq);
}

async function refreshViewerStates(
  roomId: string,
  afterSeq: number,
): Promise<void> {
  try {
    const response = await getGameApi().getViewerStates(roomId, afterSeq);
    if (response.success && response.data) {
      const adopted = adoptLatestViewerState(response.data, afterSeq);
      if (adopted) notify();
      return;
    }
    if (!response.success) {
      state = {
        ...state,
        lastError: response.error ?? "게임 상태를 불러오지 못했습니다.",
      };
      notify();
    }
  } catch (error) {
    state = {
      ...state,
      lastError:
        error instanceof Error
          ? error.message
          : "게임 상태를 불러오지 못했습니다.",
    };
    notify();
  }
}

export const multiplayerGameActions = {
  initializeAsHost: async (
    roomId: string,
    participantUserIds: readonly string[],
  ): Promise<void> => {
    const myId = getCurrentUserId() ?? "";
    const [p1Id, p2Id] = participantUserIds;
    const myPlayerId = derivePlayerIdFromParticipants(myId, participantUserIds);

    if (isGamePlatformMode()) {
      state = {
        ...initialState,
        roomId,
        myPlayerId,
        participantUserIds: [...participantUserIds],
        isHost: true,
      };
      notify();

      const existing = await getGameApi().getViewerStates(roomId);
      const existingRows =
        existing.success && existing.data ? existing.data : [];
      if (existingRows.length > 0) {
        adoptLatestViewerState(existingRows, 0);
        notify();
        return;
      }

      state = {
        ...state,
        needsHostSetup: true,
        pendingInit: {
          p1UserId: p1Id ?? "",
          p2UserId: p2Id ?? "",
        },
      };
      notify();
      return;
    }

    const legacyMyPlayerId: PlayerId = myPlayerId ?? "P2";

    // Reconnect path: if events already exist for this room (e.g. host reloaded
    // mid-game), replay from the log instead of submitting a new init.
    const eventsResponse = await getGameApi().getGameEvents(roomId);
    const existing =
      eventsResponse.success && eventsResponse.data ? eventsResponse.data : [];
    if (existing.length > 0) {
      state = {
        ...initialState,
        roomId,
        myPlayerId: legacyMyPlayerId,
        participantUserIds: [...participantUserIds],
        isHost: true,
      };
      notify();
      await multiplayerGameActions.replayEvents(roomId);
      return;
    }

    // Fresh game: defer the `init` event until the host picks a game mode on
    // the pre-game screen. submitInit() is called from that screen.
    state = {
      ...initialState,
      roomId,
      myPlayerId: legacyMyPlayerId,
      participantUserIds: [...participantUserIds],
      isHost: true,
      needsHostSetup: true,
      pendingInit: {
        seed: Date.now(),
        p1UserId: p1Id ?? "",
        p2UserId: p2Id ?? "",
      },
    };
    notify();
  },

  submitInit: async (mode: WarchestGameMode): Promise<void> => {
    const pending = state.pendingInit;
    const roomId = state.roomId;
    const myPlayerId = state.myPlayerId;
    if (!pending || !roomId || !myPlayerId) return;

    if (isGamePlatformMode()) {
      state = { ...state, needsHostSetup: false };
      notify();
      try {
        const response = await getGameApi().submitEvent(roomId, "init", {
          mode,
        });
        if (!response.success) {
          state = {
            ...state,
            needsHostSetup: true,
            lastError: response.error ?? "게임 초기화에 실패했습니다.",
          };
          notify();
          return;
        }
        await refreshViewerStates(roomId, state.lastAppliedSeq);
      } catch (error) {
        state = {
          ...state,
          needsHostSetup: true,
          lastError:
            error instanceof Error
              ? error.message
              : "게임 초기화에 실패했습니다.",
        };
        notify();
      }
      return;
    }

    const { seed = Date.now(), p1UserId, p2UserId } = pending;
    const fullState =
      mode === "draft" ? initDraftGame({ seed }) : reconstructFromSeed(seed);
    // Optimistically render the board, but keep `pendingInit` until the event
    // is persisted so a failed submit can roll back to the mode-select screen.
    state = {
      ...state,
      needsHostSetup: false,
      gameState: redactGameState(fullState, myPlayerId),
    };
    notify();

    try {
      const response = await getGameApi().submitEvent(roomId, "init", {
        seed,
        p1UserId,
        p2UserId,
        mode,
      });
      if (response.success && response.data) {
        state = {
          ...state,
          pendingInit: null,
          lastAppliedSeq: response.data.sequenceNumber,
        };
        notify();
      } else {
        state = {
          ...state,
          gameState: null,
          needsHostSetup: true,
          lastError: "게임 초기화에 실패했습니다.",
        };
        notify();
      }
    } catch (error) {
      // Roll back to the mode-select screen so the host can retry; the guest
      // never received an init event, so the optimistic board is unusable.
      state = {
        ...state,
        gameState: null,
        needsHostSetup: true,
        lastError:
          error instanceof Error
            ? error.message
            : "게임 초기화에 실패했습니다.",
      };
      notify();
    }
  },

  initializeAsGuest: async (
    roomId: string,
    participantUserIds: readonly string[] = [],
  ): Promise<void> => {
    const myPlayerId = derivePlayerIdFromParticipants(
      getCurrentUserId() ?? "",
      participantUserIds,
    );
    state = {
      ...initialState,
      roomId,
      myPlayerId,
      participantUserIds: [...participantUserIds],
      isHost: false,
    };
    notify();

    if (isGamePlatformMode()) {
      await refreshViewerStates(roomId, 0);
      return;
    }

    // Guest reload mid-game: WS subscription is brand new (not an
    // onReconnect), so we must replay from the event log here. Mirrors the
    // host-init replay path added in PR #516. Without this, a reloaded guest
    // gets stuck on the Loading… screen until a new event arrives.
    await multiplayerGameActions.replayEvents(roomId);
  },

  replayEvents: async (roomId: string): Promise<void> => {
    try {
      if (isGamePlatformMode()) {
        await refreshViewerStates(roomId, state.lastAppliedSeq);
        return;
      }

      const response = await getGameApi().getGameEvents(roomId);
      if (!response.success || !response.data) return;

      let gameState: WarchestState | null = null;
      let myPlayerId = state.myPlayerId;
      let lastSeq = 0;
      const events: HistoryEntry[] = [];

      for (const event of response.data) {
        const payload = event.payload as Record<string, unknown>;
        if (event.eventType === "init") {
          gameState = initStateFromPayload(payload);
          if (myPlayerId === null) {
            const myId = getCurrentUserId() ?? "";
            myPlayerId = derivePlayerId(myId, payload.p1UserId as string);
          }
        } else if (gameState) {
          gameState = applyEventToState(gameState, event.eventType, payload);
        }
        lastSeq = event.sequenceNumber;
        events.push({
          sequenceNumber: event.sequenceNumber,
          eventType: event.eventType,
          payload,
        });
      }

      // Guard: if no events came back, don't wipe already-initialized state
      if (lastSeq === 0) return;

      const redacted =
        gameState && myPlayerId
          ? redactGameState(gameState, myPlayerId)
          : gameState;
      state = {
        ...state,
        gameState: redacted,
        myPlayerId,
        lastAppliedSeq: lastSeq,
        events,
        needsHostSetup: false,
        pendingInit: null,
      };
      notify();
    } catch (error) {
      state = {
        ...state,
        lastError:
          error instanceof Error
            ? error.message
            : "이벤트를 불러오지 못했습니다.",
      };
      notify();
    }
  },

  applyRemoteEvent: (
    eventType: string,
    payload: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;

    if (isGamePlatformMode()) {
      state = {
        ...state,
        events: appendEvent(eventType, payload, seq),
      };
      notify();
      if (state.roomId) {
        void refreshViewerStates(state.roomId, state.lastAppliedSeq);
      }
      return;
    }

    if (!state.gameState && eventType !== "init") return;

    let myPlayerId = state.myPlayerId;

    // Derive player ID for guest on the init event
    if (eventType === "init" && myPlayerId === null) {
      const myId = getCurrentUserId() ?? "";
      myPlayerId = derivePlayerId(myId, payload.p1UserId as string);
    }

    // Always start fresh on init — either from injected state (E2E) or from seed.
    // Never pass a (possibly-redacted) state into applyEventToState for init.
    const fullState =
      eventType === "init"
        ? initStateFromPayload(payload)
        : state.gameState
          ? applyEventToState(state.gameState, eventType, payload)
          : null;

    const nextGameState =
      fullState && myPlayerId
        ? redactGameState(fullState, myPlayerId)
        : fullState;

    // For init events that re-seed the game (e.g. E2E state-injection at seq=2),
    // reset the event history — the new init defines a fresh game origin.
    const nextEvents =
      eventType === "init"
        ? [{ sequenceNumber: seq, eventType, payload }]
        : [...state.events, { sequenceNumber: seq, eventType, payload }];

    state = {
      ...state,
      gameState: nextGameState,
      myPlayerId,
      lastAppliedSeq: seq,
      selectedCoin: null,
      legalTargets: [],
      events: nextEvents,
      needsHostSetup: false,
      pendingInit: null,
    };
    notify();
  },

  applyRemoteViewerState: (
    payloadState: Record<string, unknown>,
    seq: number,
  ): void => {
    if (!isGamePlatformMode()) return;
    const adopted = adoptViewerState(payloadState, seq);
    if (adopted) notify();
  },

  selectCoin: (coinType: string | null): void => {
    state = { ...state, selectedCoin: coinType, legalTargets: [] };
    notify();
  },

  submitAction: async (
    action: WarchestAction,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!state.gameState || !state.roomId)
      return { success: false, error: "게임이 없습니다." };
    const v = validateAction(state.gameState, action);
    if (!v.valid) return { success: false, error: v.error };

    const eventType = actionToEventType(action);
    try {
      const response = await getGameApi().submitEvent(
        state.roomId,
        eventType,
        action as unknown as Record<string, unknown>,
      );
      if (!response.success) {
        return {
          success: false,
          error: response.error ?? "제출에 실패했습니다.",
        };
      }
      if (isGamePlatformMode()) {
        await refreshViewerStates(state.roomId, state.lastAppliedSeq);
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "제출에 실패했습니다.",
      };
    }
    return { success: true };
  },

  reset: (): void => {
    state = { ...initialState };
    notify();
  },
};

export const multiplayerGameStore = {
  getState: () => state,
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useMultiplayerGameStore() {
  return useSyncExternalStore(
    multiplayerGameStore.subscribe,
    multiplayerGameStore.getState,
  );
}
